import { useEffect, useState } from "react";
import { ExternalLink, CalendarDays, Newspaper, Link2 } from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import Footer from "../../components/layout/Footer";
import { api } from "../../lib/api";

function filterNext30Days(holidays) {
  const now = new Date();
  const cutoff = new Date();
  cutoff.setDate(now.getDate() + 30);
  return holidays.filter(h => {
    const d = new Date(h.date);
    return d >= now && d <= cutoff;
  });
}

/* ---- Skeleton card ---- */
function SkeletonCard({ className = "" }) {
  return (
    <div className={`bg-white border border-gray-100 rounded-xl overflow-hidden animate-pulse ${className}`}>
      <div className="aspect-[16/10] bg-gray-100" />
      <div className="p-5 space-y-3">
        <div className="h-3 bg-gray-100 rounded w-1/3" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-3 bg-gray-100 rounded w-full" />
        <div className="h-3 bg-gray-100 rounded w-5/6" />
      </div>
    </div>
  );
}

export default function UpdatesPage() {
  const [links, setLinks] = useState([]);
  const [news, setNews] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingHolidays, setLoadingHolidays] = useState(true);

  useEffect(() => {
    api.get("/important-links")
      .then(r => setLinks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLinks([]))
      .finally(() => setLoadingLinks(false));

    api.get("/news?limit=12")
      .then(r => setNews(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNews([]))
      .finally(() => setLoadingNews(false));

    api.get("/holidays/upcoming")
      .then(r => setHolidays(filterNext30Days(Array.isArray(r.data) ? r.data : [])))
      .catch(() => setHolidays([]))
      .finally(() => setLoadingHolidays(false));
  }, []);

  return (
    <div className="App">
      <PublicNav />

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12" style={{ background: "#1A1A1A" }}>
        <div className="max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-5"
            style={{ background: "rgba(220,42,28,0.2)", color: "#DC2A1C" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#DC2A1C" }} /> Updates
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-3">Updates</h1>
          <p className="text-lg max-w-2xl" style={{ color: "rgba(255,255,255,0.6)" }}>
            Important links, news and upcoming holidays all in one place.
          </p>
        </div>
      </section>

      {/* Important Links */}
      <section className="py-12 md:py-16 px-6 md:px-12" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 flex items-center gap-2" style={{ color: "#DC2A1C" }}>
              <Link2 className="w-4 h-4" /> Important Links
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>Resources &amp; Portals</h2>
          </div>

          {loadingLinks ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : links.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(220,42,28,0.08)" }}>
                <Link2 className="w-7 h-7" style={{ color: "#DC2A1C" }} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">No Links Added Yet</div>
                <div className="text-sm text-gray-400">Important trade portal links will appear here.</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {links.map(link => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col"
                >
                  {link.image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-gray-50">
                      <img
                        src={link.image_url}
                        alt={link.title}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] flex items-center justify-center" style={{ background: "rgba(220,42,28,0.06)" }}>
                      <ExternalLink className="w-10 h-10" style={{ color: "#DC2A1C", opacity: 0.4 }} />
                    </div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold text-sm mb-1" style={{ color: "#1A1A1A" }}>{link.title}</h3>
                    {link.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 flex-1">{link.description}</p>
                    )}
                    <div className="flex items-center gap-1 mt-3 text-xs font-semibold" style={{ color: "#DC2A1C" }}>
                      Visit <ExternalLink className="w-3 h-3" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest News */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 flex items-center gap-2" style={{ color: "#DC2A1C" }}>
              <Newspaper className="w-4 h-4" /> Latest News
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>The latest from our world.</h2>
          </div>

          {loadingNews ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : news.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=700&q=75", title: "Global Air Cargo Demand Rises", snippet: "Industry forecasts show increased demand for air freight across Asia-Pacific and EMEA corridors." },
                { img: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=700&q=75", title: "New Express Routes Launched", snippet: "Translog Express expands its express delivery network with new last-mile partnerships in South Asia." },
                { img: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=700&q=75", title: "Warehousing Capacity Expanded", snippet: "Our bonded warehouse facility has doubled its capacity to serve growing import and export volumes." },
              ].map((item) => (
                <article key={item.title} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm opacity-60">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-[0.15em] font-semibold mb-2" style={{ color: "#DC2A1C" }}>Coming Soon</div>
                    <h3 className="font-semibold text-lg mb-2 leading-snug" style={{ color: "#1A1A1A" }}>{item.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3">{item.snippet}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-list">
              {news.map((n) => (
                <article key={n.id} className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                  {n.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-[0.15em] font-semibold mb-2" style={{ color: "#DC2A1C" }}>
                      {new Date(n.date).toLocaleDateString("en", { month: "long", day: "numeric", year: "numeric" })}
                    </div>
                    <h3 className="font-semibold text-lg mb-2 leading-snug" style={{ color: "#1A1A1A" }}>{n.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3">{n.content}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Holidays */}
      <section className="py-12 md:py-16 px-6 md:px-12" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 flex items-center gap-2" style={{ color: "#DC2A1C" }}>
              <CalendarDays className="w-4 h-4" /> Holiday Schedule
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>Upcoming holidays (next 30 days).</h2>
          </div>

          {loadingHolidays ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-5 animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : holidays.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(220,42,28,0.08)" }}>
                <CalendarDays className="w-7 h-7" style={{ color: "#DC2A1C" }} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">No Upcoming Holidays</div>
                <div className="text-sm text-gray-400">No upcoming holidays in the next 30 days.</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {holidays.map((h) => (
                <div key={h.id} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-lg text-white grid place-items-center flex-shrink-0" style={{ background: "#DC2A1C" }}>
                      <div className="text-center leading-none">
                        <div className="text-lg font-black">{new Date(h.date).getDate()}</div>
                        <div className="text-[10px] uppercase tracking-wider mt-0.5">{new Date(h.date).toLocaleString("en", { month: "short" })}</div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold" style={{ color: "#1A1A1A" }}>{h.title}</div>
                      {h.region && <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 mt-1">{h.region}</div>}
                      {h.description && <div className="text-sm text-gray-600 mt-1">{h.description}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
